import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography as MuiTypography,
  Alert,
  Grid,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Autocomplete,
  TextField,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useInventoriesList, useInventorySummary } from '../hooks/queries/useInventories';
import { useArticlesDropdown } from '../hooks/queries/useDropdowns';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const STOCK_COLOR = '#2196F3';

const INVENTORY_FILTER_SCHEMA = {
  reference:   { type: 'text', param: 'reference' },
  designation: { type: 'text', param: 'designation' },
  category:    { type: 'text', param: 'category' },
  unit:        { type: 'text', param: 'unit' },
} as const;

const InventoryManagement: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Filter states
  const [filterArticleId, setFilterArticleId] = useState<number | null>(null);
  const { filters, update, reset, toQueryParams } = useColumnFilters(INVENTORY_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cached inventory list + summary; articles filter reuses the shared dropdown
  // cache. Data is served instantly from cache on navigation, then refreshed in
  // the background (inventory is derived from stock movements, so stay current).
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      articleId: filterArticleId || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filterArticleId, filters]);

  const listQuery = useInventoriesList(listParams);
  const { data: summary } = useInventorySummary();
  const { data: articles = [] } = useArticlesDropdown();

  const inventories = listQuery.data?.items ?? [];
  const totalCount = listQuery.data?.total ?? 0;
  const loading = listQuery.isInitialLoading;
  const summaryStats = {
    totalValue: Number(summary?.total_value) || 0,
    lowStockCount: summary?.low_stock_count || 0,
  };

  const handleFilter = () => {
    setPage(0); // param change auto-refetches via the query hook
  };

  const handleClearFilters = () => {
    setFilterArticleId(null);
    setSearchTerm('');
    reset();
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getStockStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'stock faible':
        return <WarningIcon />;
      case 'rupture de stock':
        return <ErrorIcon />;
      case 'stock normal':
        return <CheckCircleIcon />;
      default:
        return <InventoryIcon />;
    }
  };

  const getStockStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status?.toLowerCase()) {
      case 'stock faible':
        return 'warning';
      case 'rupture de stock':
        return 'error';
      case 'stock normal':
        return 'success';
      default:
        return 'default';
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Inventaires
      </Typography>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Inventaire Automatique</strong> - Cet inventaire est mis à jour automatiquement chaque fois que vous créez un nouveau produit, une entrée de stock ou une sortie de stock. Plus besoin de calculs manuels !
        </Typography>
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Articles
              </Typography>
              <Typography variant="h5" component="div">
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Articles en Alerte
              </Typography>
              <Typography variant="h5" component="div" color="warning.main">
                {summaryStats.lowStockCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Valeur Totale Stock
              </Typography>
              <Typography variant="h5" component="div" color="primary.main">
                {summaryStats.totalValue.toFixed(2)} DH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          placeholder="Rechercher par référence ou désignation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={articles || []}
              getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
              value={(articles || []).find(p => p.id === filterArticleId) || null}
              onChange={(_, article) => setFilterArticleId(article?.id || null)}
              renderInput={(params) => <TextField {...params} label="Filtrer par article" variant="filled" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={handleFilter}
              fullWidth
              startIcon={<RefreshIcon />}
            >
              Actualiser
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="outlined" onClick={handleClearFilters} fullWidth>
              Effacer les filtres
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={getModuleTableTheme('stock')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Référence
                  <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={STOCK_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Désignation
                  <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={STOCK_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Catégorie
                  <ColumnFilter type="text" value={filters.category} onChange={(v) => update('category', v)} label="catégorie" color={STOCK_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Unité
                  <ColumnFilter type="text" value={filters.unit} onChange={(v) => update('unit', v)} label="unité" color={STOCK_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="right">Total Entrées</TableCell>
              <TableCell align="right">Total Sorties</TableCell>
              <TableCell align="right">Total Retours</TableCell>
              <TableCell align="right">Stock Final</TableCell>
              <TableCell align="right">Prix Unitaire (CUMP)</TableCell>
              <TableCell align="right">Valeur Stock</TableCell>
              <TableCell>Statut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventories.map((inventory, index) => (
              <TableRow key={inventory.article_id || index}>
                <TableCell><strong>{inventory.article_reference}</strong></TableCell>
                <TableCell>{inventory.article_designation}</TableCell>
                <TableCell>
                  <Chip label={inventory.category} size="small" variant="filled" />
                </TableCell>
                <TableCell>{inventory.unit}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>
                  +{inventory.total_entries}
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -{inventory.total_exits}
                </TableCell>
                <TableCell align="right" sx={{ color: 'info.main' }}>
                  +{inventory.total_returns || 0}
                </TableCell>
                <TableCell align="right">
                  <strong>{inventory.stock_final}</strong>
                </TableCell>
                <TableCell align="right">
                  {inventory.cump ? inventory.cump.toFixed(2) + ' DH' : '-'}
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {inventory.value ?
                      inventory.value.toFixed(2) + ' DH' :
                      (inventory.cump ?
                        (inventory.stock_final * inventory.cump).toFixed(2) + ' DH' :
                        '0.00 DH'
                      )}
                  </strong>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStockStatusIcon(inventory.stock_status)}
                    label={inventory.stock_status || 'stock normal'}
                    color={getStockStatusColor(inventory.stock_status)}
                    size="small"
                    variant="filled"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />


      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        onClose={hideAlert}
      />
    </Box>
  );
};

export default InventoryManagement;